// statusColorResolution.js
//
// The two rules that decide what colour a work order's status is drawn in.
//
// Extracted from workOrderStatusesContext.js so they can be proven rather than inspected: they are
// the core of Filipp's "the status colours must match everywhere" (2026-09-16), and both of them
// used to be wrong in ways that produced exactly the mismatches he was seeing.
import { COLORS } from './theme';

/**
 * The workflow a work order's status must be read against.
 *
 * THE BUG THIS FIXES. Call sites passed `wo.workflowId` straight through. A legacy work order has
 * none, and the resolver then fell back to the default workflow of the PROVIDER's primary company
 * — which, for a job owned by the second company, is a different workflow and so a different
 * colour. The Work Orders list already resolved its GROUP HEADERS the correct way while its row
 * pills did not, so one screen could draw a group dot and the status pill inside that group in two
 * different colours for the same status.
 *
 * `getDefaultWorkflowId` is passed in rather than imported: it depends on live subscription state
 * that only the provider has.
 */
export const workflowIdForWorkOrder = (workOrder, getDefaultWorkflowId) => {
  if (workOrder && workOrder.workflowId) return workOrder.workflowId;
  const companyId = workOrder && workOrder.companyId;
  return typeof getDefaultWorkflowId === 'function' ? getDefaultWorkflowId(companyId) : undefined;
};

/**
 * The colour for a status key, given the statuses of its workflow.
 *
 * THE BUG THIS FIXES. While the workflows were still loading, the status list fell back to the
 * five hardcoded legacy statuses — so a key present in both (new, complete, ...) resolved to the
 * LEGACY colour and then swapped to the configured one the instant the subscription delivered.
 * On a slow load the same job could be two different colours on two screens a second apart, which
 * is the most likely source of "the colours don't match everywhere" as a lived experience. A
 * neutral placeholder reads as "still loading" rather than as a confident wrong answer.
 *
 * This is the LOADING state only. A company that genuinely has no workflow document is not
 * loading, and still gets its real defaults.
 *
 * The unresolvable case stays amber rather than grey, pairing with the "Needs Attention" label the
 * context gives the same case — a status deleted out from under a live job is a real thing that
 * needs a human, not a neutral.
 */
export const resolveStatusColorFrom = (statusKey, statuses, loading = false) => {
  if (loading) return COLORS.textFaint;
  const match = (statuses || []).find((s) => s && s.key === statusKey);
  if (match && match.color) return match.color;
  return statusKey ? COLORS.warning : COLORS.primary;
};
